import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { InvoiceAPI, SettingsAPI, ReceiptAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDate, STATUS_OPTIONS, STATUS_STYLES } from "../lib/format";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import Logo from "../components/Logo";
import {
  DownloadSimple,
  PencilSimple,
  ArrowLeft,
  EnvelopeSimple,
  Phone,
  Globe,
  MapPin,
  Trash,
  FilePdf,
  Image as ImageIcon,
  Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";


function ReceiptChip({ receipt }) {
  const isPdf = (receipt.content_type || "").includes("pdf");
  const [opening, setOpening] = useState(false);

  const open = async () => {
    setOpening(true);
    try {
      const url = await ReceiptAPI.previewUrl(receipt.id);
      window.open(url, "_blank", "noopener");
      // Note: not revoking immediately — new tab needs the blob to live
    } catch {
      toast.error("Couldn't open receipt");
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={opening}
      className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-muted/50 border border-border rounded-sm hover:bg-background hover:border-foreground/40 transition-colors disabled:opacity-50"
      data-testid={`receipt-link-${receipt.id}`}
    >
      {isPdf ? <FilePdf size={12} weight="bold" /> : <ImageIcon size={12} weight="bold" />}
      <span className="max-w-[160px] truncate">{receipt.original_filename || "receipt"}</span>
      <Eye size={11} weight="bold" className="opacity-60" />
    </button>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const printableRef = useRef(null);

  const reload = () => {
    Promise.all([InvoiceAPI.get(id), SettingsAPI.get()])
      .then(([inv, s]) => {
        setInvoice(inv);
        setSettings(s);
      })
      .catch(() => toast.error("Failed to load invoice"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      const updated = await InvoiceAPI.setStatus(id, newStatus);
      setInvoice(updated);
      toast.success(`Marked as ${newStatus}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await InvoiceAPI.remove(id);
      toast.success("Invoice deleted");
      setConfirmDelete(false);
      navigate(invoice?.is_subcontractor_invoice && user?.role === "admin" ? "/submissions" : "/invoices");
    } catch {
      toast.error("Failed to delete invoice");
      setDeleting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!printableRef.current) return;
    setDownloading(true);
    try {
      const node = printableRef.current;
      node.classList.add("pdf-rendering");

      // Wait for all images (including the proxied logo) to finish loading
      const images = Array.from(node.querySelectorAll("img"));
      await Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) return resolve();
              img.addEventListener("load", () => resolve(), { once: true });
              img.addEventListener("error", () => resolve(), { once: true });
            })
        )
      );

      // Force a desktop-width render so PDFs look identical on phone & desktop
      const PDF_RENDER_WIDTH = 820;
      const prevWidth = node.style.width;
      const prevMinWidth = node.style.minWidth;
      node.style.width = `${PDF_RENDER_WIDTH}px`;
      node.style.minWidth = `${PDF_RENDER_WIDTH}px`;

      let canvas;
      try {
        canvas = await html2canvas(node, {
          scale: 2,
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
          width: PDF_RENDER_WIDTH,
          windowWidth: PDF_RENDER_WIDTH,
        });
      } finally {
        node.style.width = prevWidth;
        node.style.minWidth = prevMinWidth;
      }

      const imgData = canvas.toDataURL("image/png");

      // A4 in mm: 210 x 297
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      const pageW = 210;
      const pageH = 297;
      const imgRatio = canvas.width / canvas.height;
      const pageRatio = pageW / pageH;

      let renderW, renderH;
      if (imgRatio > pageRatio) {
        // wider than page → fit width
        renderW = pageW;
        renderH = pageW / imgRatio;
      } else {
        // taller than page → fit height (ensures single page)
        renderH = pageH;
        renderW = pageH * imgRatio;
      }
      const offsetX = (pageW - renderW) / 2;
      const offsetY = (pageH - renderH) / 2;

      pdf.addImage(imgData, "PNG", offsetX, offsetY, renderW, renderH, undefined, "FAST");
      pdf.save(`${invoice.invoice_number}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate PDF");
    } finally {
      if (printableRef.current) printableRef.current.classList.remove("pdf-rendering");
      setDownloading(false);
    }
  };

  if (loading || !invoice || !settings) {
    return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div data-testid="invoice-detail-page">
      {/* Action bar — hidden in print */}
      <div className="no-print flex flex-wrap items-center justify-between gap-3 pb-4 mb-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/invoices")}
            className="rounded-sm"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft size={16} weight="bold" className="mr-1.5" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div>
            <div className="overline text-muted-foreground">Invoice</div>
            <div className="font-mono text-base sm:text-lg font-bold">
              {invoice.invoice_number}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={invoice.status} onValueChange={handleStatusChange}>
            <SelectTrigger
              className={`w-32 sm:w-36 rounded-sm border ${STATUS_STYLES[invoice.status] || ""}`}
              data-testid="detail-status-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Link to={`/invoices/${id}/edit`}>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              data-testid="edit-invoice-btn"
            >
              <PencilSimple size={16} weight="bold" className="sm:mr-1.5" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
          </Link>

          <Button
            onClick={() => setConfirmDelete(true)}
            variant="outline"
            size="sm"
            className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            data-testid="delete-invoice-btn"
          >
            <Trash size={16} weight="bold" className="sm:mr-1.5" />
            <span className="hidden sm:inline">Delete</span>
          </Button>

          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            size="sm"
            className="rounded-sm"
            data-testid="print-invoice-btn"
          >
            <DownloadSimple size={16} weight="bold" className="mr-1.5" />
            {downloading ? "Generating…" : "PDF"}
          </Button>
        </div>
      </div>

      {/* Printable invoice */}
      <article
        ref={printableRef}
        className="invoice-printable bg-white border border-border max-w-[820px] mx-auto p-5 sm:p-8 lg:p-10"
        data-testid="invoice-printable"
      >
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-5 border-b-2 border-foreground">
          <Logo size="lg" logoUrl={settings.logo_url} proxied />
          <div className="text-left sm:text-right">
            <div className="overline text-muted-foreground">Invoice</div>
            <div className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1">
              {invoice.invoice_number}
            </div>
            <div className="mt-2">
              <span
                className={`inline-block text-xs font-semibold px-2 py-0.5 border ${
                  STATUS_STYLES[invoice.status] || ""
                }`}
              >
                {invoice.status}
              </span>
            </div>
          </div>
        </header>

        {/* Company + client */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mt-5 sm:mt-6">
          <div>
            <div className="overline text-muted-foreground mb-2">From</div>
            <div className="font-display font-bold text-base mb-1">
              {invoice.issuer_name || settings.company_name || "HEO IT Engineering"}
            </div>
            {invoice.issuer_address && (
              <div className="text-sm whitespace-pre-line text-muted-foreground">
                {invoice.issuer_address}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
              {invoice.issuer_email && (
                <div className="flex items-center gap-1.5">
                  <EnvelopeSimple size={11} weight="bold" />
                  {invoice.issuer_email}
                </div>
              )}
              {invoice.issuer_phone && (
                <div className="flex items-center gap-1.5">
                  <Phone size={11} weight="bold" />
                  {invoice.issuer_phone}
                </div>
              )}
              {invoice.issuer_website && (
                <div className="flex items-center gap-1.5">
                  <Globe size={11} weight="bold" />
                  {invoice.issuer_website}
                </div>
              )}
              {invoice.issuer_vat_number && (
                <div className="font-mono">VAT: {invoice.issuer_vat_number}</div>
              )}
              {invoice.issuer_registration && (
                <div className="font-mono">Reg: {invoice.issuer_registration}</div>
              )}
            </div>
          </div>

          <div>
            <div className="overline text-muted-foreground mb-2">Bill To</div>
            <div className="font-display font-bold text-base mb-1">
              {invoice.client_name}
            </div>
            {invoice.client_address && (
              <div className="text-sm whitespace-pre-line text-muted-foreground flex gap-1">
                <MapPin size={12} weight="bold" className="mt-0.5 shrink-0" />
                <span>{invoice.client_address}</span>
              </div>
            )}
            {invoice.client_email && (
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                <EnvelopeSimple size={11} weight="bold" />
                {invoice.client_email}
              </div>
            )}
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-3 gap-px bg-border border border-border mt-6">
          <div className="bg-white px-3 py-2">
            <div className="overline text-muted-foreground">Issue Date</div>
            <div className="font-mono font-semibold mt-0.5 text-sm">
              {formatDate(invoice.issue_date)}
            </div>
          </div>
          <div className="bg-white px-3 py-2">
            <div className="overline text-muted-foreground">Due Date</div>
            <div className="font-mono font-semibold mt-0.5 text-sm">
              {formatDate(invoice.due_date)}
            </div>
          </div>
          <div className="bg-white px-3 py-2">
            <div className="overline text-muted-foreground">Terms</div>
            <div className="font-mono font-semibold mt-0.5 text-sm">
              {invoice.payment_terms || "—"}
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-foreground">
                <th className="text-left py-2 pr-3 overline text-foreground font-bold">
                  {invoice.is_subcontractor_invoice ? "Day / Description" : "Description"}
                </th>
                <th className="text-right py-2 px-3 overline text-foreground font-bold w-20">
                  {invoice.is_subcontractor_invoice ? "Hours" : "Qty"}
                </th>
                <th className="text-right py-2 px-3 overline text-foreground font-bold w-28">
                  {invoice.is_subcontractor_invoice ? "Rate" : "Unit Price"}
                </th>
                {invoice.is_subcontractor_invoice && (
                  <>
                    <th className="text-right py-2 px-3 overline text-foreground font-bold w-20">
                      Miles
                    </th>
                    <th className="text-right py-2 px-3 overline text-foreground font-bold w-24">
                      Mileage
                    </th>
                    <th className="text-right py-2 px-3 overline text-foreground font-bold w-24">
                      Parking
                    </th>
                  </>
                )}
                <th className="text-right py-2 pl-3 overline text-foreground font-bold w-28">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items.map((li, idx) => {
                const work = Number(li.quantity) * Number(li.unit_price);
                const mileAmt = Number(li.mileage_amount || 0);
                const parkAmt = Number(li.parking || 0);
                const rowTotal = work + (invoice.is_subcontractor_invoice ? mileAmt + parkAmt : 0);
                return (
                  <tr key={idx} className="border-b border-border">
                    <td className="py-2 pr-3">
                      <div>{li.description || "—"}</div>
                      {invoice.is_subcontractor_invoice && li.receipts && li.receipts.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1.5 receipts-no-print">
                          {li.receipts.map((r) => (
                            <ReceiptChip key={r.id} receipt={r} />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">
                      {Number(li.quantity).toLocaleString("en-GB", {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">
                      {formatCurrency(li.unit_price, invoice.currency)}
                    </td>
                    {invoice.is_subcontractor_invoice && (
                      <>
                        <td className="py-2 px-3 text-right font-mono">
                          {Number(li.miles || 0).toLocaleString("en-GB", {
                            maximumFractionDigits: 1,
                          })}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatCurrency(mileAmt, invoice.currency)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatCurrency(parkAmt, invoice.currency)}
                        </td>
                      </>
                    )}
                    <td className="py-2 pl-3 text-right font-mono font-semibold">
                      {formatCurrency(rowTotal, invoice.currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end mt-4">
          <div className="w-full sm:w-80 space-y-1.5">
            {invoice.is_subcontractor_invoice ? (
              (Number(invoice.mileage_total || 0) > 0 || Number(invoice.parking_total || 0) > 0) && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Hours Subtotal</span>
                    <span className="font-mono font-semibold">
                      {formatCurrency(
                        Number(invoice.subtotal || 0)
                          - Number(invoice.mileage_total || 0)
                          - Number(invoice.parking_total || 0),
                        invoice.currency
                      )}
                    </span>
                  </div>
                  {Number(invoice.mileage_total || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Mileage Subtotal</span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(invoice.mileage_total, invoice.currency)}
                      </span>
                    </div>
                  )}
                  {Number(invoice.parking_total || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Parking Subtotal</span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(invoice.parking_total, invoice.currency)}
                      </span>
                    </div>
                  )}
                </>
              )
            ) : (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(invoice.subtotal, invoice.currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    VAT ({invoice.tax_rate}%)
                  </span>
                  <span className="font-mono font-semibold">
                    {formatCurrency(invoice.tax_amount, invoice.currency)}
                  </span>
                </div>
              </>
            )}
            <div className="flex justify-between items-center pt-2 border-t-2 border-foreground">
              <span className="overline">
                {invoice.is_subcontractor_invoice ? "Total Due" : "Grand Total"}
              </span>
              <span className="font-display font-black text-2xl tracking-tighter">
                {formatCurrency(invoice.total, invoice.currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        {(invoice.issuer_bank_name ||
          invoice.issuer_account_number ||
          invoice.issuer_iban) && (
          <div className="mt-6 border border-border">
            <div className="px-3 py-1.5 bg-muted border-b border-border overline text-foreground">
              Payment Details
            </div>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {invoice.issuer_bank_name && (
                <div>
                  <div className="overline text-muted-foreground">Bank</div>
                  <div className="font-mono mt-0.5">{invoice.issuer_bank_name}</div>
                </div>
              )}
              {invoice.issuer_account_name && (
                <div>
                  <div className="overline text-muted-foreground">Account Name</div>
                  <div className="font-mono mt-0.5">{invoice.issuer_account_name}</div>
                </div>
              )}
              {invoice.issuer_account_number && (
                <div>
                  <div className="overline text-muted-foreground">Account #</div>
                  <div className="font-mono mt-0.5">{invoice.issuer_account_number}</div>
                </div>
              )}
              {invoice.issuer_sort_code && (
                <div>
                  <div className="overline text-muted-foreground">Sort Code</div>
                  <div className="font-mono mt-0.5">{invoice.issuer_sort_code}</div>
                </div>
              )}
              {invoice.issuer_iban && (
                <div>
                  <div className="overline text-muted-foreground">IBAN</div>
                  <div className="font-mono mt-0.5">{invoice.issuer_iban}</div>
                </div>
              )}
              {invoice.issuer_swift_bic && (
                <div>
                  <div className="overline text-muted-foreground">SWIFT/BIC</div>
                  <div className="font-mono mt-0.5">{invoice.issuer_swift_bic}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="mt-5">
            <div className="overline text-muted-foreground mb-1.5">Notes</div>
            <p className="text-sm whitespace-pre-line text-muted-foreground">
              {invoice.notes}
            </p>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-6 pt-4 border-t border-border text-center text-xs text-muted-foreground">
          <div className="font-display font-bold">
            {invoice.issuer_name || settings.company_name || "HEO IT Engineering"}
          </div>
          <div className="mt-0.5">Thank you for your business.</div>
        </footer>
      </article>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => !deleting && setConfirmDelete(o)}>
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-mono font-semibold">{invoice.invoice_number}</span>.
              {invoice.is_subcontractor_invoice && user?.role === "admin"
                ? " The subcontractor will no longer see it on their side either."
                : " This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="cancel-invoice-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-invoice-delete-btn"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
